/**
 * Custom hook for Contagem lookup data management
 *
 * Manages loading and state for:
 * - Clientes (customers)
 * - Hospitais (establishments)
 * - Locais de Contagem (counting locations)
 * - Auditores (somente role AUDITOR — Story 2.58)
 * - Motivos de Cancelamento (cancellation reasons)
 *
 * Usage:
 * const { clientes, hospitais, auditores, ... } = useContagemLookups(hospitalId, selectedClienteIds);
 */
import { useState, useEffect, useCallback } from 'react';
import clienteService, { Cliente } from '../services/clienteService';
import estabelecimentoService from '../services/estabelecimentoService';
import userService, { User } from '../services/userService';
import apiClient from '../services/apiClient';
import type { Estabelecimento, LocalContagem } from '../types/estabelecimento';

export interface UseContagemLookupsReturn {
  // Clientes
  clientesDisponiveis: Cliente[];
  loadingClientes: boolean;
  loadClientes: () => Promise<void>;

  // Hospitais
  hospitaisDisponiveis: Estabelecimento[];
  allHospitais: Estabelecimento[]; // All hospitals (unfiltered) - for header display
  loadingHospitais: boolean;
  searchHospital: string;
  setSearchHospital: (value: string) => void;
  showSuggestions: boolean;
  setShowSuggestions: (value: boolean) => void;
  inputHospital: string;
  setInputHospital: (value: string) => void;
  loadHospitais: () => Promise<void>;

  // Locais de Contagem
  locaisContagemDisponiveis: LocalContagem[];
  loadLocaisContagem: (hospitalId: number, clienteIds?: number[]) => Promise<void>;

  // Auditores
  auditoresDisponiveis: User[];
  loadingAuditores: boolean;
  errorAuditores: string | null;

  // Motivos
  motivosOptions: any[];
}

export const useContagemLookups = (selectedHospitalId?: number, selectedClienteIds?: number[]): UseContagemLookupsReturn => {
  // Clientes state
  const [clientesDisponiveis, setClientesDisponiveis] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  // Hospitais state
  const [hospitaisDisponiveis, setHospitaisDisponiveis] = useState<Estabelecimento[]>([]);
  const [allHospitais, setAllHospitais] = useState<Estabelecimento[]>([]);
  const [loadingHospitais, setLoadingHospitais] = useState(false);
  const [searchHospital, setSearchHospital] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputHospital, setInputHospital] = useState('');

  // Locais de Contagem state
  const [locaisContagemDisponiveis, setLocaisContagemDisponiveis] = useState<LocalContagem[]>([]);

  // Auditores state
  const [auditoresDisponiveis, setAuditoresDisponiveis] = useState<User[]>([]);
  const [loadingAuditores, setLoadingAuditores] = useState(true);
  const [errorAuditores, setErrorAuditores] = useState<string | null>(null);

  // Motivos state
  const [motivosOptions, setMotivosOptions] = useState<any[]>([]);

  // Load clientes
  const loadClientes = useCallback(async () => {
    setLoadingClientes(true);
    try {
      const clientes = await clienteService.listClientes();
      setClientesDisponiveis(clientes.filter(c => c.ativo));
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setClientesDisponiveis([]);
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  // Load hospitais
  const loadHospitais = useCallback(async () => {
    setLoadingHospitais(true);
    try {
      const hospitais = await estabelecimentoService.listEstabelecimentos();
      const activeHospitais = hospitais.filter(h => h.ativo);
      setAllHospitais(activeHospitais);
      setHospitaisDisponiveis(activeHospitais); // Initially set all
    } catch (err) {
      console.error('Erro ao carregar hospitais:', err);
      setHospitaisDisponiveis([]);
      setAllHospitais([]);
    } finally {
      setLoadingHospitais(false);
    }
  }, []);

  // Load locais de contagem for a hospital
  const loadLocaisContagem = useCallback(async (hospitalId: number, clienteIds?: number[]) => {
    if (!hospitalId) {
      setLocaisContagemDisponiveis([]);
      return;
    }

    try {
      // If clienteIds provided, try to load filtered locais
      const clienteId = clienteIds && clienteIds.length > 0 ? clienteIds[0] : undefined;
      const locais = await estabelecimentoService.listLocaisContagem(hospitalId, clienteId);
      setLocaisContagemDisponiveis(locais.filter(l => l.ativo));
    } catch (err) {
      console.error('Erro ao carregar locais de contagem:', err);
      setLocaisContagemDisponiveis([]);
    }
  }, []);

  // Filter hospitals when selectedClienteIds changes
  useEffect(() => {
    // Guard: wait for allHospitais to be loaded before filtering
    if (allHospitais.length === 0) {
      return;
    }

    if (!selectedClienteIds || selectedClienteIds.length === 0) {
      // If no clients selected, show all active hospitals
      setHospitaisDisponiveis(allHospitais);
      return;
    }

    // Capture current values to avoid stale closure issues
    const currentClienteIds = [...selectedClienteIds];
    const currentHospitais = [...allHospitais];

    const filterHospitais = async () => {
      setLoadingHospitais(true);

      try {
        // 1. Fetch vinculated hospitals for each selected client
        const promises = currentClienteIds.map(clientId =>
          clienteService.getClienteHospitais(clientId)
        );
        const results = await Promise.all(promises);

        // 2. Consolidate hospital IDs (ensure number type for comparison)
        const linkedHospitalIds = new Set<number>();
        results.forEach((clienteHospitais) => {
          clienteHospitais.forEach(ch => {
            if (ch.ativo) {
              // Ensure hospital_id is treated as number for Set comparison
              linkedHospitalIds.add(Number(ch.hospital_id));
            }
          });
        });

        // 3. Filter from all available hospitals
        const filtered = currentHospitais.filter(h => linkedHospitalIds.has(Number(h.id)));
        setHospitaisDisponiveis(filtered);

      } catch (error) {
        console.error('Erro ao filtrar hospitais por cliente:', error);
        // Fallback to all hospitals to avoid blocking user
        setHospitaisDisponiveis(currentHospitais);
      } finally {
        setLoadingHospitais(false);
      }
    };

    filterHospitais();
  }, [selectedClienteIds, allHospitais]);


  // Story 2.58: Load auditores (APENAS role=AUDITOR).
  // Anteriormente trazia CONFERENTE + MASTER tambem, mas user pediu
  // apenas auditores reais no dropdown "Gerenciar Auditor" da contagem.
  useEffect(() => {
    const loadAuditores = async () => {
      setLoadingAuditores(true);
      setErrorAuditores(null);

      try {
        const auditores = await userService.getAuditoresDisponiveis();
        const auditoresAtivos = auditores.filter(u => u.ativo);

        setAuditoresDisponiveis(auditoresAtivos);

        if (auditoresAtivos.length === 0) {
          setErrorAuditores('Nenhum auditor cadastrado no sistema.');
        }
      } catch (err: any) {
        console.error('Erro ao carregar auditores:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Erro ao carregar auditores';
        setErrorAuditores(errorMessage);
        setAuditoresDisponiveis([]);
      } finally {
        setLoadingAuditores(false);
      }
    };

    loadAuditores();
  }, []);

  // Load motivos de cancelamento
  useEffect(() => {
    const loadMotivos = async () => {
      try {
        const response = await apiClient.get('/config/motivo');
        setMotivosOptions(response.data);
      } catch (err) {
        console.error('Erro ao carregar motivos:', err);
      }
    };
    loadMotivos();
  }, []);

  // Load clientes and hospitais on mount
  useEffect(() => {
    loadClientes();
    loadHospitais();
  }, [loadClientes, loadHospitais]);

  // Load locais when hospital or selected clients change
  useEffect(() => {
    if (selectedHospitalId) {
      loadLocaisContagem(selectedHospitalId, selectedClienteIds);
    }
  }, [selectedHospitalId, selectedClienteIds, loadLocaisContagem]);

  return {
    // Clientes
    clientesDisponiveis,
    loadingClientes,
    loadClientes,

    // Hospitais
    hospitaisDisponiveis,
    allHospitais,
    loadingHospitais,
    searchHospital,
    setSearchHospital,
    showSuggestions,
    setShowSuggestions,
    inputHospital,
    setInputHospital,
    loadHospitais,

    // Locais de Contagem
    locaisContagemDisponiveis,
    loadLocaisContagem,

    // Auditores
    auditoresDisponiveis,
    loadingAuditores,
    errorAuditores,

    // Motivos
    motivosOptions,
  };
};

export default useContagemLookups;